import { inject, injectable } from 'inversify';
import { type Logger, TYPES } from '@bizzdesign/sdk-internal';
import { type Genql } from '@bizzdesign/bizzdesign-sdk-type-genql';
import type { GraphqlAPIClient } from '../graphql-client/interfaces';
import {
  NOTIFY_SUBJECT_KEY,
  RESUBSCRIBE_ON_RECONNECT_KEY,
  type WebSocketWatchdogEvent,
  WebSocketWatchdogEventType,
} from '../graphql-client/realtime-context';
import {
  type LowLevelClient,
  type LowLevelClientOptions,
  type RequireErrorsForMutation,
} from './interfaces';
import { type FetchResult } from '@apollo/client/core';
import { type MaybeMasked } from '@apollo/client/masking';
import {
  catchError,
  EMPTY,
  filter,
  from,
  type Observable,
  Subject,
  type Subscription,
  switchMap,
  tap,
} from 'rxjs';

@injectable()
// @ts-ignore 'Excessive stack depth comparing types' due to Genql typing complexity
export class GenqlLowLevelClient implements LowLevelClient {
  private readonly cacheInvalidation$: Subject<WebSocketWatchdogEvent>;
  private readonly cacheInvalidationSub: Subscription;

  constructor(
    @inject(TYPES.GraphqlAPIClient)
    private readonly gqlClient: GraphqlAPIClient,
    @inject(TYPES.Logger) private readonly logger: Logger,
  ) {
    this.cacheInvalidation$ = new Subject<WebSocketWatchdogEvent>();
    this.cacheInvalidationSub = this.cacheInvalidation$
      .pipe(
        filter(
          (event) =>
            event.eventType === WebSocketWatchdogEventType.ConnectionRestored,
        ),
        switchMap((event) =>
          from(this.cacheReset()).pipe(
            tap(() =>
              this.logger.info({
                context: 'LowLevelClient',
                msg: `Apollo cache reset completed (${event.eventType})`,
              }),
            ),
            catchError((err: unknown) => {
              this.logger.error({
                context: 'LowLevelClient',
                msg: `Apollo cache reset failed (${event.eventType})`,
                error: err,
              });
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe();
  }
  query<Q extends Genql.QueryGenqlSelection>(
    args: Q,
    options?: LowLevelClientOptions,
  ): Promise<MaybeMasked<Genql.QueryResult<Q>>> {
    this.logger.debug({
      context: 'LowLevelClient',
      msg: 'query payload',
      payload: args,
    });
    return this.gqlClient.query({
      query: args,
      useStubbing: options?.useStubbing,
      cacheable: options?.cacheable,
    });
  }

  async mutation<
    MM extends RequireErrorsForMutation<Genql.MutationGenqlSelection>,
  >(
    args: MM,
    options?: LowLevelClientOptions,
  ): Promise<Genql.MutationResult<MM>> {
    this.logger.debug({
      context: 'LowLevelClient',
      msg: 'mutation payload',
      payload: args,
    });
    return this.gqlClient.mutation({
      mutation: args,
      useStubbing: options?.useStubbing,
    });
  }

  subscription<S extends Genql.SubscriptionGenqlSelection>(
    args: S,
    options?: LowLevelClientOptions,
  ): Observable<FetchResult<Genql.SubscriptionResult<S>>> {
    this.logger.debug({
      context: 'LowLevelClient',
      msg: 'mutation payload',
      payload: args,
    });
    return this.gqlClient.subscription({
      subscription: args,
      context: {
        [NOTIFY_SUBJECT_KEY]: this.cacheInvalidation$,
        [RESUBSCRIBE_ON_RECONNECT_KEY]:
          options?.resubscribeOnReconnect === true,
      },
    });
  }

  async cacheReset(): Promise<void> {
    await this.gqlClient.cacheReset();
  }

  /**
   * Releases this client's resources: unsubscribes the cache-invalidation pipeline,
   * completes the subject and disposes the underlying GraphQL client.
   */
  async destroy(): Promise<void> {
    this.cacheInvalidationSub.unsubscribe();
    this.cacheInvalidation$.complete();
    await this.gqlClient.destroy?.();
  }

  get onConnectionRestored$(): Observable<WebSocketWatchdogEvent> {
    return this.cacheInvalidation$
      .asObservable()
      .pipe(
        filter(
          (event) =>
            event.eventType === WebSocketWatchdogEventType.ConnectionRestored,
        ),
      );
  }

  get onConnectionLost$(): Observable<WebSocketWatchdogEvent> {
    return this.cacheInvalidation$
      .asObservable()
      .pipe(
        filter(
          (event) =>
            event.eventType === WebSocketWatchdogEventType.ConnectionLost,
        ),
      );
  }
}
